import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AutoTaskAuthContext } from "./AutoTaskContext";
import { timerService } from "../services/timerService";

/**
 * App-wide work-timer state for the CURRENT user.
 *
 * Because a user can have only ONE running timer at a time, the whole app
 * needs just a single source of truth: one `/timer/active` fetch, one 1s
 * ticker. Every Start/Stop button (ticket detail AND list cards) reads from
 * here instead of fetching per-instance — so a list of 50 cards makes 0 extra
 * calls, and the running clock stays consistent everywhere.
 *
 * WHO STOPS A TIMER
 *
 * The server does. A timer ends by itself when the ticket is resolved, when
 * the ticket's time_budget runs out, or when a Head stops it from the live
 * board — and all three happen in places this app may not be watching, so
 * none of them can be decided here. What this file does instead is ASK: it
 * re-reads `/timer/active` when the budget should have run out, when the app
 * comes back to the foreground, and once a minute while a timer runs. Each of
 * those reads runs the server's policy sweep, so the answer already accounts
 * for whatever should have stopped, and `auto_stopped` says why.
 *
 * The one thing deliberately NOT done here is stopping the clock locally when
 * the budget is reached. A local stop would be written as stop_reason='manual'
 * and credited to the moment the browser noticed; letting the server close it
 * records 'budget' at exactly the budget boundary, whether this tab was open
 * or shut.
 */
export const TimerContext = createContext();

// How often a running timer re-checks itself against the server. Long enough
// to be free next to the existing 5s live-board poll, short enough that a
// stopped-by-someone-else clock does not keep ticking on a wall display.
const RESYNC_MS = 60_000;

// A stop the person did not press, in words they can act on.
const STOP_REASON_TEXT = {
  budget: "Timer stopped — the ticket's time budget is used up.",
  resolved: "Timer stopped — the ticket was resolved.",
  force_stopped: "Your timer was stopped by your head.",
  auto_eod: "Timer stopped — a session cannot run longer than 8 hours.",
  switched: "Timer stopped — a timer was started on another ticket.",
};

export const TimerProvider = ({ children }) => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const userId = userInfo?.id;

  const [active, setActive] = useState(null); // { ticket_id, ticket_number, elapsed_seconds, ... }
  const [busyTicketId, setBusyTicketId] = useState(null);
  const [ready, setReady] = useState(false);
  const [notice, setNotice] = useState(null); // why a timer stopped on its own
  const [, setTick] = useState(0); // forces per-second re-render while running

  // baseline for local ticking: elapsed = baseElapsed + (now - baseAt)/1000
  const baseElapsedRef = useRef(0);
  const baseAtRef = useRef(0);

  // When the budget runs out, as a wall-clock instant. null = no budget on
  // this ticket, so nothing to count down to.
  const budgetEndsAtRef = useRef(null);

  // Guards the one resync the budget deadline is allowed to trigger. Without
  // it the 1s ticker would fire a request every second past the deadline.
  const deadlineHandledRef = useRef(false);

  const activeRef = useRef(null); // read inside callbacks without re-binding
  const inFlightRef = useRef(false); // one resync at a time

  const seed = (a) => {
    setActive(a || null);
    activeRef.current = a || null;
    baseElapsedRef.current = a?.elapsed_seconds || 0;
    baseAtRef.current = Date.now();
    const remaining =
      a && a.budget_remaining_seconds != null
        ? Number(a.budget_remaining_seconds)
        : null;
    budgetEndsAtRef.current =
      remaining === null ? null : Date.now() + remaining * 1000;
    // Already past the deadline and STILL running means the server did not
    // close it (its sweep failed, or migration 026 has not been applied yet).
    // Treating that as unhandled would fire a request every second for as long
    // as the tab stays open, so the deadline is spent: one ask, then wait for
    // the next scheduled re-check.
    deadlineHandledRef.current = remaining !== null && remaining <= 0;
  };

  /**
   * Re-read the server's answer for this user.
   *
   * Every call runs the server's policy sweep first, so this both LEARNS about
   * and TRIGGERS an overdue stop. `auto_stopped` names the reason when the
   * sweep closed something; a timer that vanished without one was stopped
   * somewhere else (a head's force-stop, or another device), which still
   * deserves a word rather than a clock that silently disappears.
   */
  const resync = useCallback(async () => {
    if (!userId || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const resp = await timerService.active(userId);
      const next = resp?.data?.active || null;
      const had = activeRef.current;
      const closed = resp?.data?.auto_stopped;
      seed(next);
      if (had && !next) {
        const reason = Array.isArray(closed)
          ? closed.find((c) => Number(c.ticket_id) === Number(had.ticket_id))
              ?.stop_reason
          : null;
        setNotice(
          STOP_REASON_TEXT[reason] ||
            `Timer stopped on ${had.ticket_number ? `#${had.ticket_number}` : "your ticket"}.`,
        );
      }
    } catch (e) {
      // A failed check is not evidence the timer stopped — leave the clock be.
      console.error("timer resync error:", e?.message);
    } finally {
      inFlightRef.current = false;
    }
  }, [userId]);

  // Fetch the user's active timer once per user (and on login change).
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setActive(null);
      activeRef.current = null;
      setReady(true);
      return;
    }
    setReady(false);
    (async () => {
      try {
        const resp = await timerService.active(userId);
        if (!cancelled) seed(resp?.data?.active);
      } catch (e) {
        if (!cancelled) seed(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Single global ticker — only runs while something is active.
  //
  // It also watches the budget deadline. Reaching it does not stop anything
  // locally; it asks the server, which closes the session at the boundary and
  // answers with no active timer. One ask per deadline, hence the guard.
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      const endsAt = budgetEndsAtRef.current;
      if (endsAt && !deadlineHandledRef.current && Date.now() >= endsAt) {
        deadlineHandledRef.current = true;
        resync();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [active, resync]);

  // Catch up whenever the app has been away, and once a minute while running.
  //
  // This is what makes a resolve from another screen, a head's force-stop, or
  // a budget that ran out while the phone was locked show up here at all: the
  // stop happened on the server, and nothing pushes it to us.
  useEffect(() => {
    if (!userId) return;

    const onWake = () => {
      if (document.visibilityState === "visible") resync();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    const poll = setInterval(() => {
      if (activeRef.current) resync();
    }, RESYNC_MS);

    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      clearInterval(poll);
    };
  }, [userId, resync]);

  const elapsedSeconds = () =>
    active
      ? baseElapsedRef.current +
        Math.floor((Date.now() - baseAtRef.current) / 1000)
      : 0;

  // Seconds of budget left on the running ticket, or null when it has none.
  const budgetRemainingSeconds = () => {
    if (!active || budgetEndsAtRef.current === null) return null;
    return Math.max(0, Math.round((budgetEndsAtRef.current - Date.now()) / 1000));
  };

  // How far past the budget the ticket is, counting this session as it ticks.
  // 0 while still inside it, null when the ticket carries no budget.
  //
  // A running session CAN be over budget: one started with the budget already
  // spent is deliberate (the app asked first), so it runs on — and then the
  // number worth showing is the overrun, not a countdown stuck at zero.
  const budgetOverrunSeconds = () => {
    const minutes = active?.time_budget_minutes;
    if (!active || minutes == null) return null;
    const spent = Number(active.logged_seconds || 0) + elapsedSeconds();
    return Math.max(0, spent - Number(minutes) * 60);
  };

  /**
   * Start timing a ticket.
   *
   * `acknowledgeBudget` is the answer to the server's question when the
   * ticket's budget is already spent: without it the start comes back refused
   * with `data.budget_exhausted`, which the caller turns into a prompt.
   */
  const start = async (ticketId, { acknowledgeBudget = false } = {}) => {
    if (!userId || !ticketId || busyTicketId) return;
    setBusyTicketId(ticketId);
    setNotice(null);
    try {
      const resp = await timerService.start({
        ticket_id: ticketId,
        user_id: userId,
        acknowledge_budget: acknowledgeBudget || undefined,
      });
      if (resp?.success && resp?.data?.active) seed(resp.data.active);
      // A refused start is worth saying out loud, EXCEPT the budget question —
      // that one is a dialog the caller is about to open, and a notice
      // underneath it would say the same thing twice.
      else if (
        resp &&
        !resp.success &&
        resp.statusMessage &&
        !resp?.data?.budget_exhausted
      ) {
        setNotice(resp.statusMessage);
      }
      return resp;
    } catch (e) {
      console.error("timer start error:", e?.message);
    } finally {
      setBusyTicketId(null);
    }
  };

  const stop = async () => {
    if (!userId || busyTicketId) return;
    const t = active?.ticket_id ?? true;
    setBusyTicketId(t);
    setNotice(null);
    try {
      const resp = await timerService.stop({ user_id: userId });
      if (resp?.success) seed(null);
      return resp;
    } catch (e) {
      console.error("timer stop error:", e?.message);
    } finally {
      setBusyTicketId(null);
    }
  };

  const value = {
    ready,
    hasActive: !!active,
    activeTicketId: active ? active.ticket_id : null,
    activeTicketNumber: active ? active.ticket_number : null,
    isRunning: (ticketId) =>
      !!active && Number(active.ticket_id) === Number(ticketId),
    isBusy: (ticketId) =>
      busyTicketId !== null &&
      (ticketId === undefined ||
        Number(busyTicketId) === Number(ticketId) ||
        busyTicketId === true),
    elapsedSeconds,
    // The budget on the ticket being timed, for the countdown next to the
    // clock. minutes is what a head typed; the seconds are what is left.
    budgetMinutes: active ? active.time_budget_minutes ?? null : null,
    budgetRemainingSeconds,
    budgetOverrunSeconds,
    // Why the clock disappeared, when the person did not stop it themselves.
    notice,
    clearNotice: () => setNotice(null),
    start,
    stop,
    // Ask the server what is true now. Screens that just changed a ticket's
    // status (resolve/reject) call this so the clock reflects it immediately
    // instead of at the next minute's poll.
    resync,
    // Clear local running state without a network call — used when the ticket
    // is resolved/rejected (the backend already auto-stops the session).
    clearActive: () => seed(null),
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export const useTimer = () => useContext(TimerContext) || {};
